import React from 'react';
import { token, getGlobalTheme } from '@atlaskit/tokens';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { IllustrationWrapper } from '../../../../../common/ui/illustration/illustration-wrapper';
import {
	transformProjectKeyText,
	truncateText,
} from '../../../../../common/ui/illustration/issue-key-text/utils';
import messages from './messages';

export type Props = {
	isTmp: boolean;
	projectName: string;
	projectKey: string;
	teamManagedIllustrationFooter: MessageDescriptor;
	companyManagedIllustrationFooter: MessageDescriptor;
};

export const JSWScrumIllustration = ({
	projectName,
	projectKey,
	isTmp,
	teamManagedIllustrationFooter,
	companyManagedIllustrationFooter,
}: Props) => {
	const { formatMessage } = useIntl();
	const { colorMode } = getGlobalTheme();
	return (
		<IllustrationWrapper data-testId="project-create-importer-upload-flow.project-create-screen.illustration.jsw.scrum.wrapper">
			<svg
				width="469"
				height="327"
				viewBox="0 0 469 327"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				aria-label={formatMessage(messages.illustrationLabel)}
			>
				<path
					d="M231.768 9.98332C275.273 12.6401 330.466 15.1635 366.484 33.4939C402.868 52.0105 406.389 50.8482 423.647 81.0084C442.896 114.649 482.028 174.233 464.666 208.437C447.246 242.756 382.018 243.524 341.213 264.155C302.326 283.815 278.659 323.507 231.768 326.188C184.451 328.893 144.593 301.044 109.367 277.408C77.2776 255.876 58.5204 253.739 38.922 225.099C14.9905 190.126 -7.34554 147.23 2.29851 108.654C12.1046 69.4304 40.0039 22.3787 89.0874 6.23079C133.696 -8.44509 183.148 7.01424 231.768 9.98332Z"
					fill="url(#paint0_linear_4253_17317)"
				/>
				<g filter="url(#filter0_dd_4253_17317)">
					<g clipPath="url(#clip0_4253_17317)">
						<rect
							x="22"
							y="14"
							width="410.164"
							height="268.961"
							rx="5.48394"
							fill={token('color.background.input', 'white')}
						/>
						<rect
							x="145.816"
							y="71.4442"
							width="277.27"
							height="105.126"
							rx="1.97111"
							fill={token('elevation.surface.sunken', '#F7F8F9')}
						/>
						<rect
							x="153.701"
							y="79.3284"
							width="39.6517"
							height="7.88446"
							rx="3.94223"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<rect
							x="153.701"
							y="158.173"
							width="39.6517"
							height="7.88446"
							rx="3.94223"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<g filter="url(#filter1_dd_4253_17317)">
							<rect
								x="153.701"
								y="92.4694"
								width="261.501"
								height="15.7689"
								rx="1.97111"
								fill={token('color.background.input', 'white')}
							/>
							<rect
								x="183.268"
								y="98.3817"
								width="57.1623"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<rect
								x="367.896"
								y="98.3817"
								width="26.2815"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<text
								fill={token('color.text.subtlest', '#626F86')}
								fontSize="6.57038"
								letterSpacing="0px"
								x="158.957"
								y="102.69"
							>
								{transformProjectKeyText(projectKey, 1)}
							</text>
							<circle
								cx="404.689"
								cy="100.353"
								r="5.2563"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
						</g>
						<g filter="url(#filter2_dd_4253_17317)">
							<rect
								x="153.701"
								y="113.494"
								width="261.501"
								height="15.7689"
								rx="1.97111"
								fill={token('color.background.input', 'white')}
							/>
							<rect
								x="183.268"
								y="119.407"
								width="57.1623"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<rect
								x="367.896"
								y="119.407"
								width="26.2815"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<text
								fill={token('color.text.subtlest', '#626F86')}
								fontSize="6.57038"
								letterSpacing="0px"
								x="158.957"
								y="123.715"
							>
								{transformProjectKeyText(projectKey, 2)}
							</text>
							<circle
								cx="404.689"
								cy="121.379"
								r="5.2563"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
						</g>
						<g filter="url(#filter3_dd_4253_17317)">
							<rect
								x="153.701"
								y="134.519"
								width="261.501"
								height="15.7689"
								rx="1.97111"
								fill={token('color.background.input', 'white')}
							/>
							<rect
								x="183.268"
								y="140.433"
								width="57.1623"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<rect
								x="367.896"
								y="140.433"
								width="26.2815"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<text
								fill={token('color.text.subtlest', '#626F86')}
								fontSize="6.57038"
								letterSpacing="0px"
								x="158.957"
								y="144.739"
							>
								{transformProjectKeyText(projectKey, 3)}
							</text>
							<circle
								cx="404.689"
								cy="142.403"
								r="5.2563"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
						</g>
						<rect
							x="153.701"
							y="187.083"
							width="39.6517"
							height="7.88446"
							rx="3.94223"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<g filter="url(#filter4_dd_4253_17317)">
							<rect
								x="153.701"
								y="200.224"
								width="261.501"
								height="15.7689"
								rx="1.97111"
								fill={token('color.background.input', 'white')}
							/>
							<rect
								x="183.268"
								y="206.137"
								width="57.1623"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<rect
								x="367.896"
								y="206.137"
								width="26.2815"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<text
								fill={token('color.text.subtlest', '#626F86')}
								fontSize="6.57038"
								letterSpacing="0px"
								x="158.957"
								y="210.444"
							>
								{transformProjectKeyText(projectKey, 4)}
							</text>
							<circle
								cx="404.689"
								cy="208.109"
								r="5.2563"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
						</g>
						<g filter="url(#filter5_dd_4253_17317)">
							<rect
								x="153.701"
								y="221.249"
								width="261.501"
								height="15.7689"
								rx="1.97111"
								fill={token('color.background.input', 'white')}
							/>
							<rect
								x="183.268"
								y="227.163"
								width="57.1623"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<rect
								x="367.896"
								y="227.163"
								width="26.2815"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<text
								fill={token('color.text.subtlest', '#626F86')}
								fontSize="6.57038"
								letterSpacing="0px"
								x="158.957"
								y="231.469"
							>
								{transformProjectKeyText(projectKey, 5)}
							</text>
							<circle
								cx="404.689"
								cy="229.134"
								r="5.2563"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
						</g>
						<g filter="url(#filter6_dd_4253_17317)">
							<rect
								x="153.701"
								y="242.274"
								width="261.501"
								height="15.7689"
								rx="1.97111"
								fill={token('color.background.input', 'white')}
							/>
							<rect
								x="183.268"
								y="248.188"
								width="57.1623"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<rect
								x="367.896"
								y="248.188"
								width="26.2815"
								height="3.94223"
								rx="1.97111"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
							<text
								fill={token('color.text.subtlest', '#626F86')}
								fontSize="6.57038"
								letterSpacing="0px"
								x="158.957"
								y="252.494"
							>
								{transformProjectKeyText(projectKey, 6)}
							</text>
							<circle
								cx="404.689"
								cy="250.159"
								r="5.2563"
								fill={token('color.background.accent.gray.subtlest', '#F1F2F4')}
							/>
						</g>
						<rect
							x="146.183"
							y="50.8418"
							width="24.9347"
							height="8.2259"
							rx="4.11295"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<rect
							x="177.856"
							y="50.8418"
							width="24.9347"
							height="8.2259"
							rx="4.11295"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<rect
							x="209.53"
							y="50.8418"
							width="32.8684"
							height="8.2259"
							rx="4.11295"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<rect
							x="249.136"
							y="50.8418"
							width="26.068"
							height="8.2259"
							rx="4.11295"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<rect
							x="145.876"
							y="32.5088"
							width="82.171"
							height="8.2259"
							rx="4.11295"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<path
							d="M22 19.1394C22 16.3225 24.2835 14.0391 27.1003 14.0391H133V283.039H27.1003C24.2835 283.039 22 280.756 22 277.939V19.1394Z"
							fill={token('elevation.surface.sunken', '#F7F8F9')}
						/>
						<path
							d="M22 14.0391H133H22ZM133 283.039H22H133ZM22 283.039V14.0391V283.039ZM133.685 14.0391V283.039H132.315V14.0391H133.685Z"
							fill={token('color.border.disabled', '#091E42')}
						/>
						<rect
							x="35.4766"
							y="30.4908"
							width="13.6007"
							height="13.6007"
							rx="3.40018"
							fill={token('color.icon.accent.blue', '#1D7AFC')}
						/>
						<text
							fill={token('color.text.subtle', '#44546F')}
							fontSize="11"
							letterSpacing="0px"
							x="54.7085"
							y="39.8719"
						>
							{truncateText(projectName, 10)}
						</text>
						<rect
							x="36.0444"
							y="69.3176"
							width="82.171"
							height="8.2259"
							rx="4.11295"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<rect
							x="35.4771"
							y="52.3171"
							width="82.171"
							height="8.2259"
							rx="4.11295"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<rect
							x="36.0444"
							y="86.319"
							width="82.171"
							height="8.2259"
							rx="4.11295"
							fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
						<text
							fill={token('color.text.subtlest', '#626F86')}
							fontSize="6.57038"
							letterSpacing="0px"
							x="35.6238"
							y="269.126"
						>
							{isTmp
								? formatMessage(teamManagedIllustrationFooter)
								: formatMessage(companyManagedIllustrationFooter)}
						</text>
					</g>
				</g>
				<defs>
					<filter
						id="filter0_dd_4253_17317"
						x="10"
						y="2"
						width="434.164"
						height="292.961"
						filterUnits="userSpaceOnUse"
						colorInterpolationFilters="sRGB"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="6" />
						<feColorMatrix
							type="matrix"
							values={
								colorMode === 'dark'
									? '0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.56 0'
									: '0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.16 0'
							}
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow_4253_17317"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values={
								colorMode === 'dark'
									? '0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0'
									: '0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.12 0'
							}
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow_4253_17317"
							result="effect2_dropShadow_4253_17317"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow_4253_17317"
							result="shape"
						/>
					</filter>
					<filter
						id="filter1_dd_4253_17317"
						x="152.701"
						y="91.4694"
						width="263.501"
						height="18.7689"
						filterUnits="userSpaceOnUse"
						colorInterpolationFilters="sRGB"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset dy="1" />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow_4253_17317"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow_4253_17317"
							result="effect2_dropShadow_4253_17317"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow_4253_17317"
							result="shape"
						/>
					</filter>
					<filter
						id="filter2_dd_4253_17317"
						x="152.701"
						y="112.494"
						width="263.501"
						height="18.7689"
						filterUnits="userSpaceOnUse"
						colorInterpolationFilters="sRGB"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset dy="1" />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow_4253_17317"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow_4253_17317"
							result="effect2_dropShadow_4253_17317"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow_4253_17317"
							result="shape"
						/>
					</filter>
					<filter
						id="filter3_dd_4253_17317"
						x="152.701"
						y="133.519"
						width="263.501"
						height="18.7689"
						filterUnits="userSpaceOnUse"
						colorInterpolationFilters="sRGB"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset dy="1" />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow_4253_17317"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow_4253_17317"
							result="effect2_dropShadow_4253_17317"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow_4253_17317"
							result="shape"
						/>
					</filter>
					<filter
						id="filter4_dd_4253_17317"
						x="152.701"
						y="199.224"
						width="263.501"
						height="18.7689"
						filterUnits="userSpaceOnUse"
						colorInterpolationFilters="sRGB"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset dy="1" />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow_4253_17317"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow_4253_17317"
							result="effect2_dropShadow_4253_17317"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow_4253_17317"
							result="shape"
						/>
					</filter>
					<filter
						id="filter5_dd_4253_17317"
						x="152.701"
						y="220.249"
						width="263.501"
						height="18.7689"
						filterUnits="userSpaceOnUse"
						colorInterpolationFilters="sRGB"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset dy="1" />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow_4253_17317"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow_4253_17317"
							result="effect2_dropShadow_4253_17317"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow_4253_17317"
							result="shape"
						/>
					</filter>
					<filter
						id="filter6_dd_4253_17317"
						x="152.701"
						y="241.274"
						width="263.501"
						height="18.7689"
						filterUnits="userSpaceOnUse"
						colorInterpolationFilters="sRGB"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset dy="1" />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow_4253_17317"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="0.5" />
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.5 0"
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow_4253_17317"
							result="effect2_dropShadow_4253_17317"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow_4253_17317"
							result="shape"
						/>
					</filter>
					<linearGradient
						id="paint0_linear_4253_17317"
						x1="74.9975"
						y1="-1.89206e-05"
						x2="228.889"
						y2="324.521"
						gradientUnits="userSpaceOnUse"
					>
						<stop offset="0.150366" stopColor="#0065FF" />
						<stop offset="1" stopColor="#D8F2F6" />
					</linearGradient>
					<clipPath id="clip0_4253_17317">
						<rect x="22" y="14" width="410.164" height="268.961" rx="5.48394" fill="white" />
					</clipPath>
				</defs>
			</svg>
		</IllustrationWrapper>
	);
};
